import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  Text,
  HStack,
  Center,
  Icon,
  Flex,
  Box,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { ContentType } from '../../types/content/content'
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons'
import { statusColors } from '../../themes/components/tag'
import { useTranslation } from 'react-i18next'

interface LearningOptionsModalProps {
  isOpen: boolean
  onClose: () => void
  onBack: () => void 
}

const LearningOptionsModal: React.FC<LearningOptionsModalProps> = ({
  isOpen,
  onClose,
  onBack,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleOptionClick = (option: string) => {
    if (option === 'self') {
      navigate('/user/questionnaire/create', {
        state: {
          selectedType: ContentType.Training,
          questions: [],
          sourceTitle: t('content.modals.learningOptions.sourceTitle.self'),
        }
      })
    } else if (option === 'external') {
      navigate('/user/questionnaire/create', {
        state: {
          selectedType: ContentType.Learning,
          isExternalLearning: true,
          questions: [],
          sourceTitle: t('content.modals.learningOptions.sourceTitle.external'),
        }
      })
    } 
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <ModalContent maxW="600px" borderRadius="16px" boxShadow="0 8px 32px rgba(0,0,0,0.12)">
        <ModalHeader position="relative" px="32px" pt="32px" pb="24px">
          <Flex align="center" justify="flex-start">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              aria-label={t('common.actions.back')}
              borderRadius="8px"
              _hover={{ bg: 'solid.25' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
              zIndex={2}
            >
              <Icon as={ArrowBackIcon} boxSize={5} color="solid.700" />
            </Button>
            <Center position="absolute" left="0" right="0" pointerEvents="none">  
              <Text fontSize="20px" fontWeight="700" textAlign="center" color="solid.900">
                {t('content.modals.learningOptions.title')}
              </Text>
            </Center>
          </Flex>
         <ModalCloseButton
           top="20px"
           right="20px"
           sx={{
             border: 'none',
             bg: 'transparent',
             _hover: { bg: 'solid.25', borderRadius: '6px' },
             _focus: { outline: 'none', boxShadow: 'none', border: 'none' },
           }}
         />
        </ModalHeader>
        <ModalBody py="24px" px="32px" pb="32px">
          <HStack spacing="16px" justify="center">
            <Box
              width="200px"
              height="160px"
              bg="solid.0"
              borderRadius="12px"
              border="2px solid"
              borderColor="solid.100"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              cursor="pointer"
              transition="all 0.2s"
              position="relative"
              role="group"
              gap="12px"
              _hover={{
                transform: 'translateY(-4px)',
                bg: statusColors.error.bg,
                borderColor: statusColors.error.border,
                boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
              }}
              onClick={() => handleOptionClick('self')}
            >
              <Icon
                as={AddIcon}
                width="32px"
                height="32px"
                color="solid.600"
                _groupHover={{ color: statusColors.error.color }}
              />
              <Text fontSize="16px" fontWeight="700" color="solid.900" textAlign="center" px="16px" _groupHover={{ color: statusColors.error.color }}>
                {t('content.modals.learningOptions.options.self')}
              </Text>
            </Box>

            <Box
              width="200px"
              height="160px"
              bg="solid.0"
              borderRadius="12px"
              border="2px solid"
              borderColor="solid.100"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              cursor="pointer"
              transition="all 0.2s"
              position="relative"
              role="group"
              gap="12px"
              _hover={{
                transform: 'translateY(-4px)',
                bg: statusColors.error.bg,
                borderColor: statusColors.error.border,
                boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
              }}
              onClick={() => handleOptionClick('external')}
            >
              <Icon
                as={AddIcon}
                width="32px"
                height="32px"
                color="solid.600"
                _groupHover={{ color: statusColors.error.color }}
              />
              <Text fontSize="16px" fontWeight="700" color="solid.900" textAlign="center" px="16px" _groupHover={{ color: statusColors.error.color }}>
                {t('content.modals.learningOptions.options.external')}
              </Text>
            </Box>

            {/* <Box
              width="140px"
              height="140px"
              bg="gray.50"
              borderRadius="8px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              cursor="not-allowed"
              transition="all 0.2s"
              position="relative"
              opacity={0.6}
            >
              <Icon as={AddIcon} position="absolute" top="8px" right="8px" width="20px" height="20px" color="solid.600" />
              <Text textStyle="b-s" color="solid.900" textAlign="center">
                Create E-Learning with AI
              </Text>
            </Box> */}
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default LearningOptionsModal